import { supabase } from '../lib/supabase';

export class TaxConfigurationService {
  // Tax Jurisdictions Management
  static async getAllJurisdictions(organizationId) {
    const { data, error } = await supabase?.from('tax_jurisdictions')?.select(`
        *,
        tax_rates:tax_rates(count)
      `)?.eq('organization_id', organizationId)?.eq('is_active', true)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      organizationId: row?.organization_id,
      createdBy: row?.created_by,
      name: row?.name,
      jurisdictionCode: row?.jurisdiction_code,
      jurisdictionType: row?.jurisdiction_type,
      countryCode: row?.country_code,
      stateCode: row?.state_code,
      countyName: row?.county_name,
      cityName: row?.city_name,
      zipCode: row?.zip_code,
      description: row?.description,
      isActive: row?.is_active,
      taxRatesCount: row?.tax_rates?.[0]?.count || 0,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  }

  static async createJurisdiction(jurisdictionData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('tax_jurisdictions')?.insert({
        organization_id: jurisdictionData?.organizationId,
        created_by: user?.id,
        name: jurisdictionData?.name,
        jurisdiction_code: jurisdictionData?.jurisdictionCode,
        jurisdiction_type: jurisdictionData?.jurisdictionType,
        country_code: jurisdictionData?.countryCode,
        state_code: jurisdictionData?.stateCode,
        county_name: jurisdictionData?.countyName,
        city_name: jurisdictionData?.cityName,
        zip_code: jurisdictionData?.zipCode,
        description: jurisdictionData?.description
      })?.select()?.single();
    
    if (error) throw error;
    return this.mapJurisdictionFromDB(data);
  }

  static async updateJurisdiction(id, updateData) {
    const { data, error } = await supabase?.from('tax_jurisdictions')?.update({
        name: updateData?.name,
        jurisdiction_code: updateData?.jurisdictionCode,
        jurisdiction_type: updateData?.jurisdictionType,
        country_code: updateData?.countryCode,
        state_code: updateData?.stateCode,
        county_name: updateData?.countyName,
        city_name: updateData?.cityName,
        zip_code: updateData?.zipCode,
        description: updateData?.description,
        is_active: updateData?.isActive
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return this.mapJurisdictionFromDB(data);
  }

  // Tax Rates Management
  static async getAllTaxRates(organizationId) {
    const { data, error } = await supabase?.from('tax_rates')?.select(`
        *,
        jurisdiction:tax_jurisdictions!tax_rates_jurisdiction_id_fkey(name, jurisdiction_type)
      `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      organizationId: row?.organization_id,
      createdBy: row?.created_by,
      jurisdictionId: row?.jurisdiction_id,
      jurisdictionName: row?.jurisdiction?.name || 'Unknown',
      jurisdictionType: row?.jurisdiction?.jurisdiction_type || 'unknown',
      taxName: row?.tax_name,
      taxCode: row?.tax_code,
      taxRate: parseFloat(row?.tax_rate),
      calculationMethod: row?.calculation_method,
      appliesTo: row?.applies_to || [],
      exemptionRules: row?.exemption_rules || {},
      effectiveDate: row?.effective_date,
      expirationDate: row?.expiration_date,
      status: row?.status,
      reverseChargeEnabled: row?.reverse_charge_enabled,
      multiCurrencyEnabled: row?.multi_currency_enabled,
      notes: row?.notes,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  }

  static async createTaxRate(taxRateData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('tax_rates')?.insert({
        organization_id: taxRateData?.organizationId,
        created_by: user?.id,
        jurisdiction_id: taxRateData?.jurisdictionId,
        tax_name: taxRateData?.taxName,
        tax_code: taxRateData?.taxCode,
        tax_rate: taxRateData?.taxRate,
        calculation_method: taxRateData?.calculationMethod,
        applies_to: taxRateData?.appliesTo,
        exemption_rules: taxRateData?.exemptionRules,
        effective_date: taxRateData?.effectiveDate,
        expiration_date: taxRateData?.expirationDate,
        reverse_charge_enabled: taxRateData?.reverseChargeEnabled,
        multi_currency_enabled: taxRateData?.multiCurrencyEnabled,
        notes: taxRateData?.notes
      })?.select()?.single();
    
    if (error) throw error;
    return this.mapTaxRateFromDB(data);
  }

  static async updateTaxRate(id, updateData) {
    const { data, error } = await supabase?.from('tax_rates')?.update({
        tax_name: updateData?.taxName,
        tax_code: updateData?.taxCode,
        tax_rate: updateData?.taxRate,
        calculation_method: updateData?.calculationMethod,
        applies_to: updateData?.appliesTo,
        exemption_rules: updateData?.exemptionRules,
        effective_date: updateData?.effectiveDate,
        expiration_date: updateData?.expirationDate,
        status: updateData?.status,
        reverse_charge_enabled: updateData?.reverseChargeEnabled,
        multi_currency_enabled: updateData?.multiCurrencyEnabled,
        notes: updateData?.notes
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return this.mapTaxRateFromDB(data);
  }

  static async deleteTaxRate(id) {
    const { error } = await supabase?.from('tax_rates')?.delete()?.eq('id', id);
    
    if (error) throw error;
  }

  // Compliance Rules Management
  static async getAllComplianceRules(organizationId) {
    const { data, error } = await supabase?.from('tax_compliance_rules')?.select(`
        *,
        jurisdiction:tax_jurisdictions!tax_compliance_rules_jurisdiction_id_fkey(name, jurisdiction_type)
      `)?.eq('organization_id', organizationId)?.order('next_filing_date', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      organizationId: row?.organization_id,
      createdBy: row?.created_by,
      jurisdictionId: row?.jurisdiction_id,
      jurisdictionName: row?.jurisdiction?.name || 'Unknown',
      ruleName: row?.rule_name,
      ruleType: row?.rule_type,
      filingFrequency: row?.filing_frequency,
      filingDeadlineDay: row?.filing_deadline_day,
      filingDeadlineMonth: row?.filing_deadline_month,
      thresholdAmount: parseFloat(row?.threshold_amount || 0),
      automatedCalculation: row?.automated_calculation,
      notificationDaysBefore: row?.notification_days_before,
      status: row?.status,
      nextFilingDate: row?.next_filing_date,
      lastFiledDate: row?.last_filed_date,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  }

  static async createComplianceRule(ruleData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('tax_compliance_rules')?.insert({
        organization_id: ruleData?.organizationId,
        created_by: user?.id,
        jurisdiction_id: ruleData?.jurisdictionId,
        rule_name: ruleData?.ruleName,
        rule_type: ruleData?.ruleType,
        filing_frequency: ruleData?.filingFrequency,
        filing_deadline_day: ruleData?.filingDeadlineDay,
        filing_deadline_month: ruleData?.filingDeadlineMonth,
        threshold_amount: ruleData?.thresholdAmount,
        automated_calculation: ruleData?.automatedCalculation,
        notification_days_before: ruleData?.notificationDaysBefore,
        next_filing_date: ruleData?.nextFilingDate
      })?.select()?.single();
    
    if (error) throw error;
    return this.mapComplianceRuleFromDB(data);
  }

  // Tax Calculations
  static async getActiveTaxRatesForJurisdiction(jurisdictionId) {
    const { data, error } = await supabase?.rpc('get_active_tax_rates_for_jurisdiction', {
        jurisdiction_uuid: jurisdictionId
      });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      taxName: row?.tax_name,
      taxCode: row?.tax_code,
      taxRate: parseFloat(row?.tax_rate),
      appliesTo: row?.applies_to || []
    })) || [];
  }

  static async calculateTotalTaxRate(jurisdictionId, transactionType = 'sales') {
    const { data, error } = await supabase?.rpc('calculate_total_tax_rate', {
        jurisdiction_uuid: jurisdictionId,
        transaction_type: transactionType
      });
    
    if (error) throw error;
    return parseFloat(data || 0);
  }

  // Tax Rate History
  static async getTaxRateHistory(taxRateId) {
    const { data, error } = await supabase?.from('tax_rate_history')?.select(`
        *,
        changed_by_user:user_profiles!tax_rate_history_changed_by_fkey(email, full_name)
      `)?.eq('tax_rate_id', taxRateId)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      taxRateId: row?.tax_rate_id,
      organizationId: row?.organization_id,
      changedBy: row?.changed_by,
      changedByUser: row?.changed_by_user?.full_name || row?.changed_by_user?.email || 'Unknown',
      changeType: row?.change_type,
      oldValues: row?.old_values,
      newValues: row?.new_values,
      changeReason: row?.change_reason,
      effectiveDate: row?.effective_date,
      createdAt: row?.created_at
    })) || [];
  }

  // Helper methods for data mapping
  static mapJurisdictionFromDB(row) {
    return {
      id: row?.id,
      organizationId: row?.organization_id,
      createdBy: row?.created_by,
      name: row?.name,
      jurisdictionCode: row?.jurisdiction_code,
      jurisdictionType: row?.jurisdiction_type,
      countryCode: row?.country_code,
      stateCode: row?.state_code,
      countyName: row?.county_name,
      cityName: row?.city_name,
      zipCode: row?.zip_code,
      description: row?.description,
      isActive: row?.is_active,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    };
  }

  static mapTaxRateFromDB(row) {
    return {
      id: row?.id,
      organizationId: row?.organization_id,
      createdBy: row?.created_by,
      jurisdictionId: row?.jurisdiction_id,
      taxName: row?.tax_name,
      taxCode: row?.tax_code,
      taxRate: parseFloat(row?.tax_rate),
      calculationMethod: row?.calculation_method,
      appliesTo: row?.applies_to || [],
      exemptionRules: row?.exemption_rules || {},
      effectiveDate: row?.effective_date,
      expirationDate: row?.expiration_date,
      status: row?.status,
      reverseChargeEnabled: row?.reverse_charge_enabled,
      multiCurrencyEnabled: row?.multi_currency_enabled,
      notes: row?.notes,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    };
  }

  static mapComplianceRuleFromDB(row) {
    return {
      id: row?.id,
      organizationId: row?.organization_id,
      createdBy: row?.created_by,
      jurisdictionId: row?.jurisdiction_id,
      ruleName: row?.rule_name,
      ruleType: row?.rule_type,
      filingFrequency: row?.filing_frequency,
      filingDeadlineDay: row?.filing_deadline_day,
      filingDeadlineMonth: row?.filing_deadline_month,
      thresholdAmount: parseFloat(row?.threshold_amount || 0),
      automatedCalculation: row?.automated_calculation,
      notificationDaysBefore: row?.notification_days_before,
      status: row?.status,
      nextFilingDate: row?.next_filing_date,
      lastFiledDate: row?.last_filed_date,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    };
  }
}

export default TaxConfigurationService;